/**
 * Notes tab — meeting/insight notes scoped to the engagement, clickable.
 * Behavior-preserving extraction from EngagementDetail.
 */
import { previewText } from "@/lib/noteTypes";
import AttachmentBadge from "@/components/AttachmentBadge";
import ActivityPill from "@/components/ActivityPill";

export default function EngagementNotesTab({ notes, onOpenNote }) {
  // Iter 85.41 · Build an activity-title lookup once from the incoming
  // notes so any meeting note with an `activity_briefing_id` in the
  // engagement's Notes tab renders a hoverable "Activity · <title>"
  // pill. Cheap O(n) walk — the tab rarely holds more than a few dozen
  // rows even on active engagements.
  const activityTitleBy = {};
  for (const n of notes || []) {
    if (n && n.note_type === "activity_briefing") activityTitleBy[n.id] = n.title;
  }
  return (
    <div className="mt-0 space-y-3">
      {notes.length === 0 && (
        <div className="border border-border bg-card p-8 text-center text-xs text-muted-foreground">
          No notes for this engagement yet. Add notes from the Notes tab.
        </div>
      )}
      {notes.map((n) => {
        const ms = n.meeting_structured || {};
        const decisions = (ms.decisions || []).length;
        const actions = (ms.action_items || []).length;
        return (
          <button
            key={n.id}
            onClick={() => onOpenNote(n)}
            data-testid={`engagement-note-row-${n.id}`}
            className="w-full text-left border border-border bg-card p-5 hover:border-primary/60 hover:bg-secondary/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-2 flex-wrap">
                  <span>{n.note_type}</span>
                  <AttachmentBadge note={n} />
                  <ActivityPill activityId={n.activity_briefing_id} activityTitleBy={activityTitleBy} testidSuffix={n.id} />
                </div>
                <div className="font-display font-bold text-base mt-0.5">{n.title}</div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{ms.summary || previewText(n.body)}</p>
              </div>
              <div className="text-right text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                {n.meeting_date && <div>{new Date(n.meeting_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>}
                {(decisions > 0 || actions > 0) && (
                  <div className="mt-1 text-primary">{decisions} dec · {actions} act</div>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
